import { colors } from '@apollo/brand';
import { gql, useQuery } from '@apollo/client';
import {
  Badge,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
  customColors,
} from '@apollo/orbit';
import React, { useMemo } from 'react';

import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import { NoData } from 'src/components/common/noData/NoData';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Resolution } from 'src/lib/graphqlTypes/types';

const PersistedQueryOperationStatsFragment = gql`
  fragment PersistedQueryOperationStatsFragment on ServiceQueryStatsRecord {
    timestamp
    metrics {
      totalRequestCount
    }
  }
`;

const PersistedQueryRequestChartQuery = gql`
  query PersistedQueryRequestChartQuery(
    $graphId: ID!
    $graphVariant: String!
    $pqId: String!
  ) {
    graph(id: $graphId) {
      id
      statsWindow(from: -86400, resolution: R15M) {
        queryStats(
          filter: { schemaTag: $graphVariant, persistedQueryId: $pqId }
        ) {
          ...PersistedQueryOperationStatsFragment
        }
      }
    }
  }
  ${PersistedQueryOperationStatsFragment}
`;

export const PersistedQueryRequestChart = ({
  graphId,
  graphVariant,
  persistedQueryId,
}: {
  graphId: string;
  graphVariant?: string;
  persistedQueryId?: string;
}) => {
  const {
    data: newData,
    previousData,
    loading,
  } = useQuery<
    GraphQLTypes.PersistedQueryRequestChartQuery,
    GraphQLTypes.PersistedQueryRequestChartQueryVariables
  >(PersistedQueryRequestChartQuery, {
    variables: {
      graphId,
      graphVariant: graphVariant as string,
      pqId: persistedQueryId as string,
    },
    skip: !graphVariant || !persistedQueryId,
  });

  const requestData = (newData ?? previousData)?.graph?.statsWindow
    ?.queryStats as
    | GraphQLTypes.PersistedQueryOperationStatsFragment[]
    | undefined;

  return (
    <PersistedQueryRequestChartCard
      requestData={requestData}
      loading={loading}
    />
  );
};

export const PersistedQueryRequestChartCard = ({
  requestData,
  loading,
}: {
  requestData: GraphQLTypes.PersistedQueryOperationStatsFragment[] | undefined;
  loading?: boolean;
}) => {
  const stacks = useMemo(
    (): LineChartStackConfig<GraphQLTypes.PersistedQueryOperationStatsFragment>[] => [
      {
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.brand.accent,
        label: 'Requests',
        unit: undefined,
        data: requestData ?? [],
        hasData: (stats: GraphQLTypes.PersistedQueryOperationStatsFragment) =>
          stats.metrics?.totalRequestCount > 0,
        getValue: (
          stats: GraphQLTypes.PersistedQueryOperationStatsFragment,
        ) => ({
          timestamp: stats.timestamp,
          value: stats.metrics.totalRequestCount,
        }),
      },
    ],
    [requestData],
  );

  const hasData = (requestData ?? []).some(
    (stats) => stats.metrics.totalRequestCount > 0,
  );

  return (
    <Card variant="unstyled" className="py-2">
      <CardHeader className="pb-6">
        <CardTitle>
          Requests <Badge variant="info">Last day</Badge>
        </CardTitle>
        <CardDescription>
          The number of times this operation has been requested.
        </CardDescription>
      </CardHeader>
      {loading ? (
        <CardBody className="flex min-h-32 items-center justify-center pt-0">
          <Spinner size="sm" />
        </CardBody>
      ) : !hasData ? (
        <CardBody className="flex min-h-32 items-center justify-center pt-0">
          <NoData message="No operation executions found in the last day" />
        </CardBody>
      ) : (
        <CardBody className="min-h-32">
          <LineChart
            stacks={stacks}
            stacked
            strokeWidth={1}
            resolution={Resolution.R15M}
            tickIntervalMinutes={120}
            allowSingleXAxisValue
            hideCursorArrow
          />
        </CardBody>
      )}
    </Card>
  );
};
